import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import ConceptMapVisualization from '../components/ConceptMapVisualization';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-sonner-toast';
import { getConceptMapData } from '../lib/wordUsageApi';
import { ConceptMapData } from '../types/wordUsage';

const ConceptMap: React.FC = () => {
  const [conceptMapData, setConceptMapData] = useState<ConceptMapData>({ nodes: [], edges: [] });
  const [loading, setLoading] = useState<boolean>(true);
  const [filter, setFilter] = useState<string>('all');
  const [connectionFilter, setConnectionFilter] = useState<string>('all');
  const { toast } = useToast();

  useEffect(() => {
    fetchConceptMapData();
  }, []);

  const fetchConceptMapData = async () => {
    try {
      setLoading(true);
      const data = await getConceptMapData();
      setConceptMapData(data);
    } catch (error) {
      console.error('Error fetching concept map data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load concept map data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchConceptMapData();
  };

  const applyFilters = (data: ConceptMapData, forgeFilter: string, connFilter: string) => {
    // Filter nodes by forge type
    let filteredNodes = data.nodes.filter(
      (node) =>
        forgeFilter === 'all' ||
        node.forgeTypes.some((type) => type.toLowerCase() === forgeFilter.toLowerCase())
    );

    // Filter nodes by connection count
    if (connFilter !== 'all') {
      filteredNodes = filteredNodes.filter((node) => {
        const connectionCount = node.connectionCount || 0;
        switch (connFilter) {
          case 'high':
            return connectionCount >= 10;
          case 'medium':
            return connectionCount >= 5 && connectionCount <= 9;
          case 'low':
            return connectionCount >= 1 && connectionCount <= 4;
          default:
            return true;
        }
      });
    }

    // Get the IDs of the filtered nodes
    const nodeIds = new Set(filteredNodes.map((node) => node.id));

    // Filter edges to only include connections between filtered nodes
    const filteredEdges = data.edges.filter(
      (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target)
    );

    return { nodes: filteredNodes, edges: filteredEdges };
  };

  const handleFilterChange = (value: string) => {
    setFilter(value);
    applyAllFilters(value, connectionFilter);
  };

  const handleConnectionFilterChange = (value: string) => {
    setConnectionFilter(value);
    applyAllFilters(filter, value);
  };

  const applyAllFilters = (forgeFilter: string, connFilter: string) => {
    if (forgeFilter === 'all' && connFilter === 'all') {
      // No filtering needed, just refresh
      fetchConceptMapData();
    } else {
      // Apply filters
      setLoading(true);
      getConceptMapData()
        .then((data) => {
          const { nodes: filteredNodes, edges: filteredEdges } = applyFilters(
            data,
            forgeFilter,
            connFilter
          );

          // Recalculate the color scale for the filtered nodes
          // Define starting nodes that should be gold
          const startingNodes = ['water', 'fire', 'earth', 'air', 'dirt'];

          // Calculate min and max connection counts for relative heat map
          const connectionCounts = filteredNodes.map((node) => node.connectionCount);
          const minConnections = Math.min(...connectionCounts.filter((count) => count > 0), 1);
          const maxConnections = Math.max(...connectionCounts, 1);

          // Apply colors based on relative connection counts
          filteredNodes.forEach((node) => {
            // Check if this is a starting node
            if (startingNodes.includes(node.id.toLowerCase())) {
              node.color = '#FFD700'; // Gold color for starting nodes
            } else if (node.connectionCount > 0) {
              // Calculate relative position in the range
              const relativePosition =
                (node.connectionCount - minConnections) / (maxConnections - minConnections || 1);

              // Create a color scale from blue (few) to purple (medium) to red (many)
              if (relativePosition < 0.33) {
                // Blue to purple gradient for lower third
                const blueComponent = Math.round(59 + (128 - 59) * (1 - relativePosition * 3));
                node.color = `rgb(${blueComponent}, 130, 246)`;
              } else if (relativePosition < 0.66) {
                // Purple to red gradient for middle third
                const redComponent = Math.round(
                  128 + (220 - 128) * ((relativePosition - 0.33) * 3)
                );
                const blueComponent = Math.round(
                  246 - (246 - 128) * ((relativePosition - 0.33) * 3)
                );
                node.color = `rgb(${redComponent}, 130, ${blueComponent})`;
              } else {
                // Red gradient for upper third
                const redComponent = Math.round(
                  220 + (255 - 220) * ((relativePosition - 0.66) * 3)
                );
                const blueComponent = Math.round(
                  128 - (128 - 59) * ((relativePosition - 0.66) * 3)
                );
                node.color = `rgb(${redComponent}, 59, ${blueComponent})`;
              }
            } else {
              // Default color for nodes with no connections
              node.color = '#9CA3AF'; // Gray
            }
          });

          setConceptMapData({
            nodes: filteredNodes,
            edges: filteredEdges,
          });
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error filtering concept map data:', error);
          setLoading(false);
        });
    }
  };

  return (
    <AdminLayout title="Concept Map">
      <div className="flex flex-col h-[calc(100vh-88px)] overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4">
              <Select value={filter} onValueChange={handleFilterChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Forge Types</SelectItem>
                  <SelectItem value="item">Item Forge</SelectItem>
                  <SelectItem value="location">Location Forge</SelectItem>
                  <SelectItem value="character">Character Forge</SelectItem>
                  <SelectItem value="deconstructor">Deconstructor</SelectItem>
                </SelectContent>
              </Select>

              <Select value={connectionFilter} onValueChange={handleConnectionFilterChange}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by connections" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Connections</SelectItem>
                  <SelectItem value="high">High (10+ connections)</SelectItem>
                  <SelectItem value="medium">Medium (5-9 connections)</SelectItem>
                  <SelectItem value="low">Low (1-4 connections)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-sm text-muted-foreground hidden md:block">
              <span>Drag nodes to rearrange • Scroll to zoom • Hover for details</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        <div className="flex-grow overflow-hidden pb-6">
          <div className="w-full h-full rounded-lg border border-border shadow-sm bg-card overflow-hidden">
            <ConceptMapVisualization
              data={conceptMapData}
              loading={loading}
              width={window.innerWidth - 340 - 48} // Adjust for sidebar and padding (24px on each side) and internal padding (24px on each side)
              height={window.innerHeight - 228 - 48 - 24} // Adjust for header, filter bar, padding, and bottom margin (24px)
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ConceptMap;
